import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dateFormats } from '~/utils';

// Auto-scroll edge threshold (px) and max speed (px/frame), kept under a row height.
const EDGE = 64;
const MAX_SPEED = 8;

export default function useDrag({ allocation, dateWidth, canvas, rows, parentRef, onDrag, onUpdate, onClick, onDrop }) {
  // Drag & Drop
  const [event, setEvent] = useState(null);
  const [x, setX] = useState(allocation.meta.coords.x);
  const [y, setY] = useState(allocation.meta.coords.y);
  const [offsetLeft, setOffsetLeft] = useState(null);
  const [targetRow, setTargetRow] = useState(null);

  // Last pointer position, reused by the auto-scroll loop where no pointer event is available.
  const pointerRef = useRef(null);
  const scrollSpeedRef = useRef(0);
  const scrollRafRef = useRef(null);
  const movingRef = useRef(false);

  const { start, end } = useMemo(() => {
    const daysOffset = Math.floor((x + dateWidth / 2) / dateWidth) - Math.floor(allocation.meta.coords.x / dateWidth);

    let start;
    let end;
    switch (allocation.unit) {
      case 'day':
      case 'allocation':
      case 'ratio_of_capacity': {
        const days = moment.duration(moment(allocation.end).diff(allocation.start, 'days'), 'days').asDays();
        start = moment(allocation.start).add(daysOffset, 'days');
        end = moment(start).add(days, 'days');
        break;
      }

      case 'week': {
        start = moment(allocation.start).add(daysOffset, 'days').startOf('isoWeek');
        const weeks = moment.duration(moment(allocation.end).diff(allocation.start, 'weeks'), 'weeks').asWeeks();
        end = moment(start).add(weeks, 'weeks').endOf('isoWeek');
        break;
      }

      case 'month': {
        start = moment(allocation.start).add(daysOffset, 'days').startOf('month');
        const months = moment.duration(moment(allocation.end).diff(allocation.start, 'months'), 'months').asMonths();
        end = moment(start).add(months, 'months').endOf('month');
        break;
      }
    }

    return {
      start: start.format(dateFormats.isoDate),
      end: end.format(dateFormats.isoDate),
    };
  }, [x, dateWidth, allocation.meta.coords.x, allocation.unit, allocation.start, allocation.end]);

  // Drag & Drop
  const handleDragStart = useCallback((event) => {
    if (event.button !== 0) return;

    // Store the mouse position when clicking the the draggable element
    setOffsetLeft(event.pageX - event.currentTarget.getBoundingClientRect().left);
    setEvent('down');
  }, []);

  // Resolve the drag from a pointer coordinate; reused on each auto-scroll frame.
  const applyDrag = useCallback(
    (pageX, pageY) => {
      const container = canvas.current;
      if (!container) return;

      const coords = {
        mouse: {
          // The mouse X coordinates.
          x: pageX,

          // The mouse left offset relative to the clicked element.
          offsetLeft: offsetLeft,
        },

        canvas: {
          // The canvas' left offset relative to the page.
          offsetLeft: container.getBoundingClientRect().left,

          // The canvas' left scrolling offset.
          scrollLeft: container.scrollLeft,
        },

        element: {
          // The X coordinates of the element
          get x() {
            return coords.mouse.x - coords.mouse.offsetLeft - coords.canvas.offsetLeft + coords.canvas.scrollLeft;
          },
        },
      };

      // Snap the bar to the row under the pointer (reassignment); disabled when rows aren't provided
      let snappedY = allocation.meta.coords.y;
      let row = null;

      if (rows) {
        const canvasY = pageY - container.getBoundingClientRect().top;

        // Resolve the row the pointer is over, restricted to resource rows in the same project
        row =
          rows.find(
            (row) =>
              row.type === 'row' &&
              row.projectId === allocation.projectId &&
              canvasY >= row.meta.top &&
              canvasY < row.meta.top + row.meta.height,
          ) ?? null;

        const sourceRow = rows.find(
          (row) =>
            allocation.meta.coords.y >= row.meta.top && allocation.meta.coords.y < row.meta.top + row.meta.height,
        );

        // Keep the lane offset on snap, clamped so the bar stays inside a shorter target row
        if (row && sourceRow) {
          const laneOffset = allocation.meta.coords.y - sourceRow.meta.top;
          const maxOffset = Math.max(0, row.meta.height - allocation.meta.style.height);
          snappedY = row.meta.top + Math.min(laneOffset, maxOffset);
        }
      }

      // Prevent the drag event if neither the dates nor the target row changed
      if (Math.abs(allocation.meta.coords.x - coords.element.x) < 1 && snappedY === allocation.meta.coords.y) return;

      movingRef.current = true;
      setEvent('move');
      setX(coords.element.x);
      setY(snappedY);
      setTargetRow(row);

      // Update the tooltip position
      if (onDrag) onDrag();
    },
    [
      canvas,
      offsetLeft,
      onDrag,
      rows,
      allocation.meta.coords.x,
      allocation.meta.coords.y,
      allocation.meta.style.height,
      allocation.projectId,
    ],
  );

  const stopAutoScroll = useCallback(() => {
    scrollSpeedRef.current = 0;
    if (scrollRafRef.current) {
      window.cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  }, []);

  // Scroll one step, then re-resolve the drag so the snap follows the moving rows.
  const autoScrollTick = useCallback(() => {
    const scrollContainer = parentRef?.current;
    if (!scrollContainer || !scrollSpeedRef.current || !pointerRef.current) {
      scrollRafRef.current = null;
      return;
    }

    let lower = 0;
    let upper = scrollContainer.scrollHeight - scrollContainer.clientHeight;

    // Bound scrolling to the dragged allocation's project rows (the only valid drop targets).
    let bandTop = Infinity;
    let bandBottom = -Infinity;
    for (const row of rows ?? []) {
      if (row.type !== 'row' || row.projectId !== allocation.projectId) continue;
      bandTop = Math.min(bandTop, row.meta.top);
      bandBottom = Math.max(bandBottom, row.meta.top + row.meta.height);
    }
    if (bandBottom > -Infinity) {
      upper = Math.max(0, Math.min(upper, bandBottom - scrollContainer.clientHeight));
      lower = Math.min(bandTop, upper);
    }

    // Step in the drag direction only, clamped to the band — never reverse or jump.
    const speed = scrollSpeedRef.current;
    const current = scrollContainer.scrollTop;
    const next =
      speed > 0
        ? Math.max(current, Math.min(current + speed, upper))
        : Math.min(current, Math.max(current + speed, lower));

    // Stop at the scroll boundary.
    if (next === scrollContainer.scrollTop) {
      stopAutoScroll();
      return;
    }

    scrollContainer.scrollTop = next;
    applyDrag(pointerRef.current.pageX, pointerRef.current.pageY);
    scrollRafRef.current = window.requestAnimationFrame(autoScrollTick);
  }, [parentRef, rows, allocation.projectId, applyDrag, stopAutoScroll]);

  // Set scroll speed from the pointer's distance to the top/bottom edge.
  const updateAutoScroll = useCallback(
    (pageY) => {
      const scrollContainer = parentRef?.current;
      if (!scrollContainer || !rows) {
        stopAutoScroll();
        return;
      }

      const rect = scrollContainer.getBoundingClientRect();
      let speed = 0;

      if (pageY < rect.top + EDGE) {
        const ratio = Math.min(1, (rect.top + EDGE - pageY) / EDGE);
        speed = -Math.ceil(MAX_SPEED * ratio * ratio);
      } else if (pageY > rect.bottom - EDGE) {
        const ratio = Math.min(1, (pageY - (rect.bottom - EDGE)) / EDGE);
        speed = Math.ceil(MAX_SPEED * ratio * ratio);
      }

      scrollSpeedRef.current = speed;

      if (speed && !scrollRafRef.current) scrollRafRef.current = window.requestAnimationFrame(autoScrollTick);
      if (!speed) stopAutoScroll();
    },
    [parentRef, rows, autoScrollTick, stopAutoScroll],
  );

  const handleDrag = useCallback(
    (event) => {
      pointerRef.current = { pageX: event.pageX, pageY: event.pageY };
      applyDrag(event.pageX, event.pageY);
      // Only auto-scroll once a drag is actually underway, not on a near-edge click.
      if (movingRef.current) updateAutoScroll(event.pageY);
    },
    [applyDrag, updateAutoScroll],
  );

  const handleDragEnd = useCallback(
    async (e) => {
      stopAutoScroll();
      pointerRef.current = null;
      movingRef.current = false;
      setEvent(null);
      setOffsetLeft(null);
      setTargetRow(null);

      switch (event) {
        case 'down':
          if (onClick) onClick(allocation, e);
          break;

        case 'move':
          {
            if (onDrop) {
              onUpdate(true);
              setX(allocation.meta.coords.x);
              setY(allocation.meta.coords.y);
              await onDrop({ ...allocation, start: start, end: end }, targetRow);
              onUpdate(false);
            }
          }
          break;
      }
    },
    [event, allocation, start, end, targetRow, onClick, onDrop, onUpdate, stopAutoScroll],
  );

  useEffect(() => {
    setX(allocation.meta.coords.x);
  }, [allocation.meta.coords.x]);

  useEffect(() => {
    setY(allocation.meta.coords.y);
  }, [allocation.meta.coords.y]);

  useEffect(() => {
    if (event) {
      window.addEventListener('pointermove', handleDrag);
      window.addEventListener('pointerup', handleDragEnd);
    }

    return () => {
      window.removeEventListener('pointermove', handleDrag);
      window.removeEventListener('pointerup', handleDragEnd);
    };
  }, [event, handleDrag, handleDragEnd]);

  useEffect(() => {
    // Cancel any in-flight auto-scroll if the bar unmounts mid-drag.
    return () => stopAutoScroll();
  }, [stopAutoScroll]);

  return { handleDragStart, x, y, event, start, end, targetRow };
}
